import { Splide, SplideSlide } from '@splidejs/react-splide'
import React, { useCallback, useRef, useState } from 'react'
import './RailProfileImages.scss'
import { RowSpeed } from '../../../constants'
import useCarousel from '@hooks/useCarousel'
import { splidesPerMove } from '@constants/index'
import { ProfileImagesData } from 'sdk/services/config/types/config'
import classNames from 'classnames'
import { UserProfile } from 'sdk/services/storage/profiles/types/profilesManager'
import { setProfilePhoto } from '@actions/profileActions'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { SingularProfileCard } from '@components/Profile/SingularProfileCard/SingularProfileCard'
import { onEnterKeyPressAndClick } from '@utils/helpers'

interface RailProfileImagesProps {
  items: ProfileImagesData[]
  profileData: UserProfile
  railId: string
}

export const RailProfileImages = ({ items, profileData, railId }: RailProfileImagesProps) => {
  const nextSlide = useRef<HTMLButtonElement>(null)
  const previousSlide = useRef<HTMLButtonElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [isFocus, setIsFocus] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const onFocusLeft = currentIndex === 0 ? undefined : ''
  const onFocusRight = ''
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const moveLeft = useCallback(() => {
    previousSlide.current?.click()
  }, [])

  const moveRight = useCallback(() => {
    nextSlide.current?.click()
  }, [])

  useCarousel(carouselRef.current, moveLeft, moveRight, currentIndex)

  const onSplideMove = (index: number) => {
    setCurrentIndex(index)
    if (index !== 0 && previousSlide.current) {
      previousSlide.current.disabled = false
    }
  }

  const handleOnFocus = () => {
    setIsFocus(true)
  }

  const handleOnBlur = () => {
    setIsFocus(false)
  }

  const handleOnMouseDown = (index: number) => {
    setCurrentIndex(index)
  }

  const handleSavePhoto = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      onEnterKeyPressAndClick(e, () => {
        const selectedItem = items[currentIndex]
        dispatch(setProfilePhoto(selectedItem))
        navigate(-1)
      })
    },
    [currentIndex, dispatch, items, navigate],
  )

  return (
    <div
      className="focusable rail-profile-images scroll-center"
      tabIndex={-1}
      id={railId}
      ref={carouselRef}
      data-sn-left={onFocusLeft}
      data-sn-right={onFocusRight}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      onKeyDown={handleSavePhoto}
      onClick={handleSavePhoto}
    >
      <div className="box-shadow-splide"></div>
      <Splide
        onMove={(_splide, index) => onSplideMove(index)}
        options={{
          perMove: splidesPerMove,
          speed: RowSpeed.RAIL,
          gap: '0',
          slideFocus: false,
          pauseOnFocus: true,
          pagination: false,
          drag: false,
          focus: 0,
          lazyLoad: true,
          isNavigation: true,
          rewind: false,
        }}
      >
        {items.map((item: ProfileImagesData, index: number) => {
          const isActive = isFocus && currentIndex === index

          return (
            <SplideSlide key={`${item.slug}_${index}`} onMouseDown={() => handleOnMouseDown(index)} className="slide">
              <SingularProfileCard
                profileData={item}
                className={classNames({ active: isActive }, item.type)}
                profileName={profileData?.name}
                isFocusable={false}
              />
            </SplideSlide>
          )
        })}
        <div className="splide__arrows">
          <button className="splide__arrow splide__arrow--prev" ref={previousSlide}></button>
          <button className="splide__arrow splide__arrow--next" ref={nextSlide}></button>
        </div>
      </Splide>
    </div>
  )
}
